import BookSpecificDoctorButton from '@/components/calcom/BookSpecificDoctorButton';
import CalendarPopupButton from '@/components/calcom/CalendarPopupButton';
import settings from '@/data/constants';
import { Button, toast } from '@montugroup/design-system';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Menu, MenuItem } from '@mui/material';
import type { MouseEvent } from 'react';
import React, { useState } from 'react';

interface BookConsultationMenuProps {
  fieldsToPrefillBookingForm: any;
  onBookingSuccessful: any;
  doctorsList: any;
  doctor: any;
  enableBookAnyDoctorButton: boolean;
  enableBookSpecificDoctorButton: boolean;
  initialDoctorConsultCompleted: boolean;
  initialCompletedNurseConsults: boolean;
  isPatientFromTas?: boolean;
  isPatientActive?: boolean;
  refreshConsultationTab?: any;
}

export const BookConsultationMenu: React.FC<BookConsultationMenuProps> = (props: BookConsultationMenuProps) => {
  const {
    fieldsToPrefillBookingForm,
    onBookingSuccessful,
    doctorsList,
    doctor,
    enableBookAnyDoctorButton,
    enableBookSpecificDoctorButton,
    initialDoctorConsultCompleted,
    initialCompletedNurseConsults,
    isPatientFromTas,
    isPatientActive,
    refreshConsultationTab,
  } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const disabledBookAnyDoctorButtonText = !initialCompletedNurseConsults
    ? `the patient has not completed an 'Initial - Nurse' consult`
    : 'the patient has already been assigned a doctor';
  const disabledBookSpecificDoctorButtonText = !initialCompletedNurseConsults
    ? `the patient has not completed an 'Initial - Nurse' consult`
    : 'there is already a scheduled doctors consult';

  const handleOpen = (event: MouseEvent<HTMLButtonElement>): void => {
    refreshConsultationTab();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleBookAnyDoctor = (event: MouseEvent) => {
    if (!enableBookAnyDoctorButton) {
      // Manually stop propagation (instead of disabling the element) so element is clickable and can trigger custom message
      event.stopPropagation();
      toast.warning(`This option is disabled because ${disabledBookAnyDoctorButtonText}`);
    }
  };

  const handleBookSpecificDoctor = (event: MouseEvent) => {
    if (!enableBookSpecificDoctorButton) {
      // Manually stop propagation (instead of disabling the element) so element is clickable and can trigger custom message
      event.stopPropagation();
      toast.warning(`This option is disabled because ${disabledBookSpecificDoctorButtonText}`);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        disabled={!isPatientActive}
        color="primary"
        onClick={handleOpen}
        size="large"
        sx={{ width: { xs: '100%', md: 'auto' } }}
      >
        Book a consultation
        <ArrowDropDownIcon />
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={handleBookAnyDoctor}
          data-testid={`book-any-doctor-option${!enableBookAnyDoctorButton || isPatientFromTas ? '-disabled' : ''}`}
        >
          <CalendarPopupButton
            formData={fieldsToPrefillBookingForm}
            link={
              initialDoctorConsultCompleted
                ? settings.calendar.doctorFollowupRoundRobin
                : settings.calendar.doctorRoundRobin
            }
            onBookingSuccessful={onBookingSuccessful}
            disabled={!enableBookAnyDoctorButton || isPatientFromTas}
          >
            Book any doctor consult
          </CalendarPopupButton>
        </MenuItem>

        <MenuItem
          onClick={handleBookSpecificDoctor}
          data-testid={`book-specific-doctor-option${!enableBookSpecificDoctorButton ? '-disabled' : ''}`}
        >
          <BookSpecificDoctorButton
            isPatientFromTas={isPatientFromTas}
            formData={fieldsToPrefillBookingForm}
            doctors={doctorsList}
            assignedDoctor={doctor}
            disabled={!enableBookSpecificDoctorButton}
            disableBookingTypeButton={initialDoctorConsultCompleted}
          >
            Book specific doctor consult
          </BookSpecificDoctorButton>
        </MenuItem>
      </Menu>
    </Box>
  );
};
